import { FC, useEffect, Fragment } from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { useDependencies, provide } from '@servicetitan/react-ioc';
import { Button, Drawer, ButtonGroup, BodyText, Stack, Select } from '@servicetitan/design-system';
import { CampaignCostDrawerStore } from '../stores/campaign-cost-drawer.store';
import { CostInputs } from './cost-inputs';
import { CenteredSpinner } from '@servicetitan/marketing-ui';
import * as Styles from './campaign-cost-drawer.module.less';
import { CampaignCostApi, MarketingChannel } from '@servicetitan/application-data/marketing';

interface CampaignCostDrawerProps {
    id: number;
    name: string;
    channel: MarketingChannel;
    onSave(): void;
    onClose(): void;
}

export const CampaignCostDrawer: FC<CampaignCostDrawerProps> = provide({
    singletons: [CampaignCostDrawerStore, CampaignCostApi],
})(
    observer(({ id, name, channel, onSave, onClose }) => {
        const [store] = useDependencies(CampaignCostDrawerStore);
        const { costs, year, yearOptions, loading } = store;

        const handleClickSave = async () => {
            await store.save();
            onSave();
        };

        const getMonthIndex = (index: number) => index + 12 * year.value;

        useEffect(() => {
            store.init(id, channel);
        }, [store, id, channel]);

        return (
            <Drawer
                header="Campaign Cost"
                className="qa-campaign-cost-drawer"
                open
                onClose={onClose}
                backdrop
                width="s"
                footer={
                    <ButtonGroup>
                        <Button onClick={onClose} className="qa-campaign-cost-drawer-cancel">
                            Cancel
                        </Button>
                        <Button
                            primary
                            onClick={handleClickSave}
                            className="qa-campaign-cost-drawer-save"
                            loading={store.saving}
                        >
                            Save
                        </Button>
                    </ButtonGroup>
                }
            >
                <Stack justifyContent="space-between">
                    <BodyText bold size="large" className="m-r-2 m-t-1">
                        {name}
                    </BodyText>
                    <Select
                        value={year.value}
                        onChange={year.onChangeHandler}
                        options={yearOptions}
                        className={classnames('qa-campaign-cost-drawer-year', Styles.yearsSelect)}
                    />
                </Stack>
                {costs.$.length > 0 && !loading ? (
                    <Fragment>
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="January"
                            qaPrefix="qa-campaign-cost-drawer-january"
                            value={costs.$[getMonthIndex(0)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="February"
                            qaPrefix="qa-campaign-cost-drawer-february"
                            value={costs.$[getMonthIndex(1)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="March"
                            qaPrefix="qa-campaign-cost-drawer-march"
                            value={costs.$[getMonthIndex(2)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="April"
                            qaPrefix="qa-campaign-cost-drawer-april"
                            value={costs.$[getMonthIndex(3)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="May"
                            qaPrefix="qa-campaign-cost-drawer-may"
                            value={costs.$[getMonthIndex(4)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="June"
                            qaPrefix="qa-campaign-cost-drawer-june"
                            value={costs.$[getMonthIndex(5)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="July"
                            qaPrefix="qa-campaign-cost-drawer-july"
                            value={costs.$[getMonthIndex(6)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="August"
                            qaPrefix="qa-campaign-cost-drawer-august"
                            value={costs.$[getMonthIndex(7)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="September"
                            qaPrefix="qa-campaign-cost-drawer-september"
                            value={costs.$[getMonthIndex(8)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="October"
                            qaPrefix="qa-campaign-cost-drawer-october"
                            value={costs.$[getMonthIndex(9)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="November"
                            qaPrefix="qa-campaign-cost-drawer-november"
                            value={costs.$[getMonthIndex(10)]}
                        />
                        <hr className={Styles.divider} />
                        <CostInputs
                            title="December"
                            qaPrefix="qa-campaign-cost-drawer-december"
                            value={costs.$[getMonthIndex(11)]}
                        />
                    </Fragment>
                ) : (
                    <CenteredSpinner />
                )}
            </Drawer>
        );
    })
);
